import { useCallback, useRef, useState } from 'react';

import { ILocation } from '@rbi-ctg/frontend';
import { OrderLocatorTab } from 'state/launchdarkly/variations';

import {
  FocusedBy,
  MapViewRef,
  OnRestaurantHighlight,
  StoreLocatorAction,
  StoreLocatorActionType,
  StoreLocatorActions,
} from '../components/types';
import { IStore } from '../ducks/stores';

/**
 * Highlight store
 *
 * This hook centralizes the store highlight process.
 * First, it sets focus on the restaurant in the store locator and switches to the appropriate tab.
 * If highlighting from the map, the list scrolls to the top, ensuring the store appears at the top.
 * Finally, the store is highlighted on the map.
 */
export const useHighlightStore = ({
  storesRecent,
  storesFavs,
  storesNearby,
  onTabChange,
  onScrollToRestaurant,
  onScrollToTop,
  mapRef,
  actions,
}: {
  storesRecent: IStore[];
  storesFavs: IStore[];
  storesNearby: IStore[];
  onTabChange: (tab: OrderLocatorTab) => void;
  onScrollToRestaurant: (restaurantId: string) => void;
  onScrollToTop: () => void;
  mapRef: React.RefObject<MapViewRef | null>;
  actions: React.MutableRefObject<StoreLocatorActions>;
  closestStore: IStore | null;
  previousClosestStore: IStore | null;
  activeStore: IStore | null;
  userCoordinates: ILocation | null;
}) => {
  const [focusedRestaurant, setFocusedRestaurant] = useState<IStore | null>(null);
  const focusedByRef = useRef<'map' | 'list' | 'global'>('global');

  const handleOnRestaurantHighLight: OnRestaurantHighlight = useCallback(
    ({ restaurant, focusedBy, coords }) => {
      if (!restaurant) {
        return;
      }

      /// 1. Update the focused restaurant
      setFocusedRestaurant(restaurant);
      focusedByRef.current = focusedBy ?? 'global';
      // 2. Handle tab changes
      const restaurantId = restaurant?._id;
      if (focusedBy !== FocusedBy.LIST) {
        let tab = OrderLocatorTab.NEARBY;

        // CAUTION: Not always a restaurant is in the nearby list.
        // If restaurant is not in nearby, then check favorite and recent
        if (!storesNearby.some(r => r._id === restaurantId)) {
          if (restaurant?.isFavorite || storesFavs.some(r => r._id === restaurantId)) {
            tab = OrderLocatorTab.FAVORITE;
          } else if (storesRecent.some(r => r._id === restaurantId)) {
            tab = OrderLocatorTab.RECENT;
          }
        }

        onTabChange(tab);
        // 3. Handle list scroll if needed
        onScrollToTop();
      }

      if (focusedBy === FocusedBy.LIST && restaurantId) {
        onScrollToRestaurant(restaurantId);
      }

      // 4. Handle map focus based on action type
      const mapActions = mapRef.current;

      if (!mapActions) {
        return;
      }

      const actionConfig = {
        [StoreLocatorActionType.LOCATION_RESET]: {
          // Without this the focus falls back to `closestStore`, which still holds the previous
          // search result until the refreshed list render commits.
          restaurant,
          focusReason: StoreLocatorAction.isReset,
          coordinates: coords,
        },
        [StoreLocatorActionType.MANUAL_SEARCH]: {
          restaurant,
          focusReason: StoreLocatorAction.isManualSearch,
          coordinates: coords,
        },
        [StoreLocatorActionType.AREA_SEARCH]: {
          restaurant,
          focusReason: StoreLocatorAction.isSearching,
          coordinates: coords,
        },
        [StoreLocatorActionType.INITIAL_LOAD]: {
          focusReason: StoreLocatorAction.isInitial,
          coordinates: coords,
        },
        [StoreLocatorActionType.USER_SELECTION]: {
          restaurant,
          focusReason: StoreLocatorAction.isUserSelection,
          coordinates: coords,
        },
      };

      // Find the first active action and apply its configuration
      // Priority: USER_SELECTION should take precedence over INITIAL_LOAD when both are active
      const activeAction =
        (Object.entries(actions.current).find(
          ([key, isActive]) => isActive && key === StoreLocatorActionType.USER_SELECTION
        )?.[0] as StoreLocatorActionType) ||
        (Object.entries(actions.current).find(
          ([_, isActive]) => isActive
        )?.[0] as StoreLocatorActionType);

      if (activeAction) {
        // If USER_SELECTION is active, also deactivate INITIAL_LOAD to prevent it from canceling the zoom
        if (activeAction === StoreLocatorActionType.USER_SELECTION) {
          actions.current[StoreLocatorActionType.INITIAL_LOAD] = false;
        }
        mapActions?.focus(actionConfig[activeAction]);
        actions.current[activeAction] = false;
      }
    },
    [
      actions,
      mapRef,
      onScrollToRestaurant,
      onScrollToTop,
      onTabChange,
      storesFavs,
      storesNearby,
      storesRecent,
    ]
  );

  return {
    focusedRestaurant,
    focusedByRef,
    handleOnRestaurantHighLight,
  };
};
